/**
 * 003 — Quartal nach Zahlung (Ist), US3 / FR-020..FR-023 / FR-UI-003.
 * Read-only view of GET /bank/quarters/ist: documents with a payment share in
 * the quarter (bank evidence or manual date, labelled), totals, the bank
 * coverage status and the three gap lists. Print reuses the shell's print CSS
 * (`bank-print` / `bank-no-print`); CSV comes from the same endpoint.
 */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  EmptyState,
  GlassPanel,
  LoadingSpinner,
  StatusBadge,
  type StatusTone,
} from '../ui';
import { ApiError } from '../../services/api';
import {
  bankApi,
  bankKeys,
  day,
  ibanLabel,
  money,
  type IstReport,
  type PaymentRecord,
} from '../../lib/bank-api';
import { errorText } from './ReviewEditor';
import type { BankTabProps } from './types';

const QUARTER_RE = /^(\d{4})-Q([1-4])$/;

function parseQuarter(quarter: string): { year: number; q: number } {
  const m = QUARTER_RE.exec(quarter);
  if (m) return { year: Number(m[1]), q: Number(m[2]) };
  const now = new Date();
  return { year: now.getUTCFullYear(), q: Math.floor(now.getUTCMonth() / 3) + 1 };
}

function quarterBounds(year: number, q: number): { from: string; to: string } {
  const from = `${year}-${String((q - 1) * 3 + 1).padStart(2, '0')}-01`;
  const to = new Date(Date.UTC(year, q * 3, 0)).toISOString().slice(0, 10);
  return { from, to };
}

const KIND_LABELS: Record<'invoice' | 'expense', string> = {
  invoice: 'Rechnung',
  expense: 'Beleg',
};

const SOURCE_LABELS: Record<PaymentRecord['source'], string> = {
  bank: 'Bank-Nachweis',
  manual: 'manuell, ohne Bank-Nachweis',
};

/** Invoice statuses arrive as English codes; expense statuses are already German. */
const DOCUMENT_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  sent: { label: 'Gesendet', tone: 'info' },
  paid: { label: 'Bezahlt', tone: 'success' },
  overdue: { label: 'Überfällig', tone: 'danger' },
  deferred: { label: 'Zurückgestellt', tone: 'warning' },
  cancelled: { label: 'Storniert', tone: 'muted' },
  geprüft: { label: 'geprüft', tone: 'success' },
  archiviert: { label: 'archiviert', tone: 'muted' },
};

const statusOf = (status: string) =>
  DOCUMENT_STATUS[status] ?? { label: status.replace(/_/g, ' '), tone: 'neutral' as const };

/** Same rule as the API (payments.ts): only exactly 'EUR' is summed. */
const isEur = (currency: string) => currency === 'EUR';

const FOREIGN_FORMATS = new Map<string, Intl.NumberFormat | null>();

/**
 * Amount in the given currency. EUR goes through the shared `money()`; any other
 * code is shown with its ISO code so it can never be read as euros. The currency
 * comes from OCR/import, so an ill-formed code falls back to "<number> <code>".
 */
function moneyIn(cents: number, currency: string): string {
  if (isEur(currency)) return money(cents);
  if (!FOREIGN_FORMATS.has(currency)) {
    let format: Intl.NumberFormat | null = null;
    try {
      format = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
      });
    } catch {
      format = null;
    }
    FOREIGN_FORMATS.set(currency, format);
  }
  const format = FOREIGN_FORMATS.get(currency);
  if (format) return format.format(cents / 100);
  const number = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(cents / 100);
  return `${number} ${currency || '?'}`;
}

function QuarterPicker({
  quarter,
  onChange,
}: {
  quarter: string;
  onChange: (quarter: string) => void;
}) {
  const { year, q } = parseQuarter(quarter);
  const years = [year - 2, year - 1, year, year + 1, year + 2];
  return (
    <div className="flex flex-wrap items-end gap-3 bank-no-print" data-testid="ist-quarter-picker">
      <label className="text-sm">
        Jahr
        <select
          aria-label="Jahr"
          className="block rounded border border-white/10 bg-surface p-2"
          value={year}
          onChange={(e) => onChange(`${e.target.value}-Q${q}`)}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Quartal">
        {[1, 2, 3, 4].map((n) => {
          const active = n === q && QUARTER_RE.test(quarter);
          return (
            <Button
              key={n}
              variant={active ? 'primary' : 'secondary'}
              aria-pressed={active}
              className={active ? 'ring-1 ring-white/40 bg-white/15' : ''}
              data-testid={`ist-quarter-Q${n}`}
              onClick={() => onChange(`${year}-Q${n}`)}
            >
              Q{n}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function TotalCard({
  title,
  rows,
  testid,
}: {
  title: string;
  rows: [string, string][];
  testid: string;
}) {
  return (
    <div className="rounded border border-white/10 p-3" data-testid={testid}>
      <h3 className="text-sm font-medium text-white/80">{title}</h3>
      <dl className="mt-2 space-y-1 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-2">
            <dt className="text-white/60">{label}</dt>
            <dd className="font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Totals({ totals }: { totals: IstReport['totals'] }) {
  const kind = (t: IstReport['totals']['invoice']): [string, string][] => [
    ['Brutto', money(t.gross)],
    ['USt', money(t.vat)],
    ['Netto', money(t.net)],
    ['Anzahl', String(t.count)],
  ];
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <TotalCard title="Rechnungen" rows={kind(totals.invoice)} testid="ist-total-invoice" />
      <TotalCard title="Ausgaben" rows={kind(totals.expense)} testid="ist-total-expense" />
      <TotalCard
        title="nur manuell, ohne Bank-Nachweis"
        testid="ist-total-manual"
        rows={[
          ['Brutto', money(totals.manualOnly.gross)],
          ['USt', money(totals.manualOnly.vat)],
          ['Anzahl', String(totals.manualOnly.count)],
        ]}
      />
      <TotalCard
        title="davon ungeprüft"
        testid="ist-total-unreviewed"
        rows={[['Anzahl', String(totals.unreviewed.count)]]}
      />
      <p
        className="flex flex-wrap items-center gap-2 text-sm sm:col-span-2 lg:col-span-4"
        data-testid="ist-total-foreign"
      >
        <span className="text-white/70">Fremdwährung ohne EUR-Umrechnung, nicht summiert:</span>{' '}
        <StatusBadge tone={totals.foreignCurrency.count > 0 ? 'warning' : 'muted'}>
          {totals.foreignCurrency.count}
        </StatusBadge>
      </p>
      <p
        className="flex flex-wrap items-center gap-2 text-sm sm:col-span-2 lg:col-span-4"
        data-testid="ist-total-outgoing-flagged"
      >
        <span className="text-white/70">Als eigener Ausgangsbeleg markiert, nicht summiert:</span>{' '}
        <StatusBadge tone={totals.outgoingFlagged.count > 0 ? 'warning' : 'muted'}>
          {totals.outgoingFlagged.count}
        </StatusBadge>
      </p>
      <p className="text-xs text-white/60 sm:col-span-2 lg:col-span-4">
        Rechnungen und Ausgaben zählen nur Zahlungen mit Bank-Nachweis; manuelle Zahlungstage stehen
        separat. USt- und Netto-Anteile sind proportional zum bezahlten Bruttoanteil je Zahlung.
        Belege in Fremdwährung werden gelistet, aber in keiner EUR-Summe gezählt, solange keine
        belegte Umrechnung vorliegt. Belege, die die Erkennung als eigenen Ausgangsbeleg markiert
        hat, zählen erst, wenn eine Prüfung sie als Ausgabe bestätigt.
      </p>
    </div>
  );
}

function DocumentsTable({ documents }: { documents: IstReport['documents'] }) {
  if (documents.length === 0)
    return (
      <EmptyState
        title="Keine Zahlungen im Quartal"
        description="Für dieses Quartal liegt kein Beleg mit Zahlungsanteil vor (Bank-Nachweis oder manueller Zahlungstag)."
      />
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="ist-documents">
        <thead>
          <tr className="text-left text-white/60">
            <th className="p-2">Art</th>
            <th className="p-2">Nummer</th>
            <th className="p-2">Gegenpartei</th>
            <th className="p-2">Rechnungsdatum</th>
            <th className="p-2">Zahlungsdatum(e) im Quartal</th>
            <th className="p-2">Quelle</th>
            <th className="p-2 text-right">Bezahlt im Quartal</th>
            <th className="p-2 text-right">USt-Anteil</th>
            <th className="p-2 text-right">Netto-Anteil</th>
            <th className="p-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((d) => {
            const sources = [...new Set(d.payments.map((p) => p.source))];
            const status = statusOf(d.status);
            const foreign = !isEur(d.currency);
            // Bank evidence is the EUR movement (US3 scenario 7); a manual payment date
            // carries the document amount, i.e. the source currency.
            const amountCurrency = foreign && !sources.includes('bank') ? d.currency : 'EUR';
            return (
              <tr
                key={`${d.kind}:${d.id}`}
                className="border-t border-white/10 align-top"
                data-testid="ist-document-row"
              >
                <td className="p-2">{KIND_LABELS[d.kind]}</td>
                <td className="p-2">{d.number ?? '—'}</td>
                <td className="p-2">{d.counterparty ?? '—'}</td>
                <td className="p-2 whitespace-nowrap">
                  {d.invoiceDate ? day(d.invoiceDate) : 'Unbekannt'}
                </td>
                <td className="p-2 whitespace-nowrap">
                  {d.payments.map((p) => day(p.bookingDate)).join(', ')}
                </td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {sources.map((s) => (
                      <StatusBadge key={s} tone={s === 'bank' ? 'success' : 'warning'}>
                        {SOURCE_LABELS[s]}
                      </StatusBadge>
                    ))}
                  </div>
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  {moneyIn(d.paidInQuarterCents, amountCurrency)}
                  {foreign && amountCurrency === 'EUR' && (
                    <span className="block text-xs text-white/60">
                      Beleg: {moneyIn(d.grossCents, d.currency)}
                    </span>
                  )}
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  {moneyIn(d.vatInQuarterCents, amountCurrency)}
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  {moneyIn(d.netInQuarterCents, amountCurrency)}
                </td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                    {d.unreviewed && <StatusBadge tone="warning">ungeprüft</StatusBadge>}
                    {foreign && (
                      <StatusBadge tone="warning" data-testid="ist-foreign-currency">
                        {`${d.currency || '?'} · nicht in EUR-Summen`}
                      </StatusBadge>
                    )}
                    {d.outgoingFlag && (
                      <StatusBadge tone="warning" data-testid="ist-outgoing-flagged">
                        Ausgangsbeleg? · nicht in Summen
                      </StatusBadge>
                    )}
                  </div>
                  {d.standingInvoice && (
                    <p className="mt-1 text-xs text-amber-200" data-testid="ist-standing-note">
                      VSt-Aufteilung je Zahlung — Stbr-Bestätigung ausstehend
                    </p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GapSection({
  testid,
  title,
  description,
  count,
  emptyHint,
  children,
}: {
  testid: string;
  title: string;
  description: string;
  count: number;
  emptyHint: string;
  children: ReactNode;
}) {
  return (
    <section data-testid={testid} className="rounded border border-white/10 p-3">
      <h3 className="flex items-center justify-between gap-2 font-medium">
        <span>{title}</span>
        <StatusBadge tone={count > 0 ? 'warning' : 'muted'} data-testid={`${testid}-count`}>
          {count}
        </StatusBadge>
      </h3>
      <p className="mt-1 text-xs text-white/60">{description}</p>
      {count === 0 ? (
        <p className="mt-2 text-sm text-white/50">{emptyHint}</p>
      ) : (
        <ul className="mt-2 divide-y divide-white/10 text-sm">{children}</ul>
      )}
    </section>
  );
}

function GapLists({ gaps }: { gaps: IstReport['gaps'] }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <GapSection
        testid="ist-gap-paid-without-document"
        title="Bezahlt ohne Beleg"
        description="Bankbuchungen im Quartal ohne bestätigten Beleg (offener Restbetrag)."
        count={gaps.paidWithoutDocument.length}
        emptyHint="Keine Bankbuchungen ohne Beleg."
      >
        {gaps.paidWithoutDocument.map((g) => (
          <li key={g.transactionId} className="flex flex-wrap justify-between gap-x-3 py-1">
            <span className="whitespace-nowrap">{day(g.bookingDate)}</span>
            <span className="min-w-0 flex-1 break-words">
              {g.counterparty?.name ??
                (g.counterparty?.iban
                  ? ibanLabel(g.counterparty.iban)
                  : (g.counterparty?.reference ?? 'Gegenpartei unbekannt'))}
            </span>
            <span className="whitespace-nowrap">{money(g.amountCents)}</span>
          </li>
        ))}
      </GapSection>
      <GapSection
        testid="ist-gap-document-without-payment"
        title="Beleg ohne Zahlung"
        description="Belege bis Quartalsende ohne Zahlung oder mit unbekanntem Zahlungstag."
        count={gaps.documentWithoutPayment.length}
        emptyHint="Keine Belege ohne Zahlung."
      >
        {gaps.documentWithoutPayment.map((g) => (
          <li key={`${g.kind}:${g.id}`} className="flex flex-wrap justify-between gap-x-3 py-1">
            <span className="min-w-0 flex-1 break-words">
              {KIND_LABELS[g.kind]} {g.number ?? '—'}
            </span>
            <span className="whitespace-nowrap">
              {g.invoiceDate ? day(g.invoiceDate) : 'Datum unbekannt'}
            </span>
            <span className="whitespace-nowrap">{moneyIn(g.grossCents, g.currency ?? 'EUR')}</span>
          </li>
        ))}
      </GapSection>
      <GapSection
        testid="ist-gap-manual-only"
        title="Nur manueller Zahlungstag"
        description="Zahlungstag nur manuell erfasst, ohne bestätigte Bankbuchung."
        count={gaps.manualOnly.length}
        emptyHint="Keine Zahlungen nur mit manuellem Zahlungstag."
      >
        {gaps.manualOnly.map((g) => (
          <li key={`${g.kind}:${g.id}`} className="flex flex-wrap justify-between gap-x-3 py-1">
            <span className="min-w-0 flex-1 break-words">
              {KIND_LABELS[g.kind]} {g.number ?? '—'}
            </span>
            <span className="whitespace-nowrap">{day(g.paymentDate)}</span>
            <span className="whitespace-nowrap">{moneyIn(g.grossCents, g.currency)}</span>
          </li>
        ))}
      </GapSection>
    </div>
  );
}

function Coverage({ report }: { report: IstReport }) {
  if (report.coverage.complete)
    return (
      <div data-testid="ist-coverage" data-complete="true">
        <StatusBadge tone="success" size="md">
          Bankbasis vollständig
        </StatusBadge>
      </div>
    );
  const { year, q } = parseQuarter(report.quarter);
  const ranges = report.coverage.gaps.length > 0 ? report.coverage.gaps : [quarterBounds(year, q)];
  const text = ranges.map((g) => `${day(g.from)} – ${day(g.to)}`).join('; ');
  return (
    <p
      className="rounded border border-amber-400/40 p-3 text-sm text-amber-200"
      data-testid="ist-coverage"
      data-complete="false"
    >
      Bankbasis unvollständig — Zahlungen im Zeitraum {text} fehlen möglicherweise
    </p>
  );
}

export function IstQuarterTab(props: BankTabProps) {
  const query = useQuery({
    queryKey: bankKeys.ist(props.tenantId, props.quarter, props.account),
    queryFn: () => bankApi.ist({ quarter: props.quarter, account: props.account }),
    enabled: Boolean(props.tenantId),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
  const data = query.data;
  const csvUrl = bankApi.istCsvUrl({ quarter: props.quarter, account: props.account });
  return (
    <div className="space-y-6" data-testid="ist-quarter-tab">
      <GlassPanel>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            {/* Payments and totals are tenant-wide; an account only narrows "bezahlt ohne Beleg" (Codex #17). */}
            <h2 className="text-lg font-semibold">Quartal nach Zahlung · {props.quarter} · alle Konten</h2>
            {props.account && (
              <p className="mt-1 text-sm text-white/70" data-testid="ist-account-scope">
                Kontoauswahl {ibanLabel(props.account)} filtert nur die Liste „bezahlt ohne Beleg“; Zahlungen
                und Summen gelten für alle Konten.
              </p>
            )}
            <p className="mt-1 text-base">
              Basis:{' '}
              <strong className="font-semibold" data-testid="ist-basis">
                Zahlungsdatum (Ist)
              </strong>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 bank-no-print">
            <Button variant="secondary" onClick={() => globalThis.window.print()}>
              Drucken
            </Button>
            <a
              href={csvUrl}
              download
              data-testid="ist-csv"
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-white/70 hover:border-white/20 hover:bg-white/5 hover:text-white"
            >
              CSV exportieren
            </a>
          </div>
        </div>
        <p className="mt-3 text-sm text-white/70">
          EÜR mit Ist-Versteuerung (§ 20 UStG): maßgeblich ist der Zahlungstag;
          Fälligkeit/Rechnungsdatum sind kein Zahlungsnachweis; keine USt-Berechnung, keine
          steuerliche Freigabe.
        </p>
        <div className="mt-4">
          <QuarterPicker quarter={props.quarter} onChange={props.onQuarterChange} />
        </div>
      </GlassPanel>
      {query.isLoading && (
        <div role="status" aria-label="Quartal wird geladen" className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      )}
      {query.error && (
        <p role="alert" className="rounded border border-red-400/40 p-3" data-testid="ist-error">
          {errorText(query.error)}
        </p>
      )}
      {data && (
        <>
          <GlassPanel>
            <Coverage report={data} />
            <div className="mt-4">
              <Totals totals={data.totals} />
            </div>
          </GlassPanel>
          <GlassPanel>
            <h2 className="mb-3 text-lg font-semibold">
              Belege mit Zahlung im Quartal ({data.documents.length})
            </h2>
            <DocumentsTable documents={data.documents} />
          </GlassPanel>
          <GlassPanel>
            <h2 className="mb-3 text-lg font-semibold">Lücken</h2>
            <GapLists gaps={data.gaps} />
          </GlassPanel>
        </>
      )}
    </div>
  );
}
